<template>
  <div :class="['h-screen w-screen flex flex-col overflow-hidden bg-slate-900 text-slate-100', preferences.theme]">
    <!-- Global Loading Overlay for CPython WebAssembly Stack -->
    <GlobalPyodideLoader 
      :status="pyodideStatus"
      :message="pyodideStatusMessage"
      @retry="retryInitPyodide"
    />

    <!-- Header Navbar -->
    <HeaderNavbar 
      :pyodide-status="pyodideStatus"
      :pyodide-status-message="pyodideStatusMessage"
      :done-count="doneCount"
      :total-count="QUESTIONS.length"
      :timer-display="timerDisplay"
      :theme="preferences.theme"
      :sidebar-collapsed="preferences.sidebarCollapsed"
      :console-collapsed="preferences.consoleCollapsed"
      @open-timer="showTimerModal = true"
      @toggle-sidebar="toggleSidebar"
      @toggle-console="toggleConsole"
      @open-onboarding="showOnboardingModal = true"
      @open-shortcuts="showShortcutsModal = true"
      @open-export="showExportModal = true"
      @reset-layout="resetLayout"
      @toggle-theme="toggleTheme"
    />

    <!-- Main Workspace Area -->
    <div class="flex-1 flex overflow-hidden relative min-h-0">
      <!-- Question Navigation Sidebar -->
      <aside
        v-show="!preferences.sidebarCollapsed"
        class="h-full overflow-hidden shrink-0 relative"
        :style="{ width: preferences.sidebarWidth + 'px' }"
      >
        <QuestionSidebar 
          :questions="QUESTIONS"
          :active-question-id="activeQuestionId"
          :answer-statuses="answerStatuses"
          @select-question="switchQuestion"
          @collapse="toggleSidebar"
        />
      </aside>

      <ResizeHandle
        v-if="!preferences.sidebarCollapsed"
        direction="horizontal"
        @resize="onSidebarResize"
        @resize-end="persistLayout"
      />

      <!-- Collapsed sidebar reopen strip -->
      <button
        v-if="preferences.sidebarCollapsed"
        type="button"
        class="shrink-0 w-8 h-full bg-slate-950 border-r border-slate-800 hover:bg-slate-900 text-slate-400 hover:text-amber-300 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer"
        title="Show Question Bank"
        @click="toggleSidebar"
      >
        <i class="pi pi-bars text-sm"></i>
        <span class="text-[10px] font-semibold tracking-widest uppercase [writing-mode:vertical-rl] rotate-180">Questions</span>
      </button>

      <!-- Main Center IDE Panel -->
      <main class="flex-1 flex flex-col min-w-0 bg-slate-900 h-full overflow-hidden">
        <!-- Question Detail Sub-Header Bar -->
        <div class="bg-slate-950 border-b border-slate-800 px-4 py-2 flex items-center justify-between shrink-0 select-none">
          <div class="flex items-center space-x-3 truncate min-w-0">
            <button
              type="button"
              class="lg:hidden shrink-0 p-1.5 rounded-md bg-slate-800 border border-slate-700 text-slate-300 hover:text-amber-300 cursor-pointer"
              title="Toggle Question Bank"
              @click="toggleSidebar"
            >
              <i class="pi pi-bars text-xs"></i>
            </button>
            <span class="text-xs px-2 py-0.5 rounded font-mono font-semibold uppercase tracking-wide bg-unibo-red/20 text-unibo-red border border-unibo-red/30">
              {{ currentQuestion.category }}
            </span>
            <h2 class="text-sm font-bold text-slate-100 truncate tracking-tight">
              {{ currentQuestion.title }}
            </h2>
          </div>

          <div class="flex items-center space-x-3 shrink-0">
            <!-- Mode Switcher: Colab Notebook vs Single Script -->
            <div class="flex bg-slate-900 p-0.5 rounded-lg border border-slate-800 text-xs">
              <button 
                type="button"
                @click="setEditorMode('notebook')"
                :class="[
                  'px-2.5 py-1 rounded font-medium transition-colors flex items-center space-x-1 cursor-pointer',
                  preferences.editorMode === 'notebook' ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30' : 'text-slate-400 hover:text-slate-200'
                ]"
              >
                <i class="pi pi-book text-xs"></i>
                <span>Jupyter Notebook</span>
              </button>

              <button 
                type="button"
                @click="setEditorMode('single')"
                :class="[
                  'px-2.5 py-1 rounded font-medium transition-colors flex items-center space-x-1 cursor-pointer',
                  preferences.editorMode === 'single' ? 'bg-slate-800 text-slate-100 font-bold border border-slate-700' : 'text-slate-400 hover:text-slate-200'
                ]"
              >
                <i class="pi pi-code text-xs"></i>
                <span>Script View</span>
              </button>
            </div>

            <!-- Student Self-Assessment Status Dropdown -->
            <div class="flex items-center space-x-1.5 bg-slate-900 px-2.5 py-1 rounded-lg border border-slate-800 text-xs">
              <span class="text-slate-400 font-medium hidden sm:inline">Status:</span>
              <select 
                v-model="currentStatus" 
                @change="updateStatus"
                class="bg-slate-800 text-slate-200 border-none rounded px-2 py-0.5 text-xs font-semibold focus:ring-1 focus:ring-unibo-red focus:outline-none cursor-pointer"
              >
                <option value="not_started">⚪ Not Started</option>
                <option value="in_progress">🟡 In Progress</option>
                <option value="done">🟢 Done (Practiced)</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Split Screen: Prompt + Editor on top, Output Console on bottom -->
        <div class="flex-1 flex flex-col min-h-0">
          <!-- Top Half: Prompt & Editor (Side by Side on Desktop) -->
          <div
            class="flex min-h-0"
            :class="preferences.consoleCollapsed ? '' : 'border-b border-slate-800'"
            :style="{ flex: '1 1 auto', minHeight: '120px' }"
          >
            <!-- Left: Question Statement Markdown -->
            <div
              class="h-full overflow-hidden bg-slate-900 hidden lg:block"
              :style="{ width: preferences.promptPercent + '%', minWidth: '180px' }"
            >
              <PromptMarkdown :content="currentQuestion.promptMarkdown" />
            </div>

            <ResizeHandle
              class="hidden lg:block"
              direction="horizontal"
              @resize="onPromptResize"
              @resize-end="persistLayout"
            />

            <!-- Right: Jupyter/Colab Notebook Cell Runner OR Single CodeMirror Editor -->
            <div class="h-full overflow-hidden flex-1 min-w-[200px]">
              <NotebookCellRunner 
                v-if="preferences.editorMode === 'notebook' && questionReady"
                :key="activeQuestionId"
                :initial-code="currentCode"
                :saved-cells="notebookCells"
                :notebook-name="activeQuestionId"
                :notebook-title="currentQuestion?.title"
                @update:code="onNotebookCodeUpdated"
                @update:cells="onCellsUpdated"
                @toggle-view="toggleEditorMode"
              />
              <CodeMirrorEditor 
                v-else-if="preferences.editorMode === 'single' && questionReady"
                :key="activeQuestionId"
                v-model="currentCode"
                :is-running="isRunning"
                :theme="preferences.theme"
                :last-saved="lastSavedTime"
                @update:model-value="onScriptCodeUpdated"
                @run="handleRunCode"
                @save="manualSave"
                @reset-starter="resetStarterCode"
                @open-snapshots="showSnapshotsModal = true"
              />
              <div
                v-else
                class="h-full flex items-center justify-center text-xs text-slate-500 font-mono"
              >
                Loading notebook…
              </div>
            </div>
          </div>

          <ResizeHandle
            v-if="!preferences.consoleCollapsed"
            direction="vertical"
            @resize="onConsoleResize"
            @resize-end="persistLayout"
          />

          <!-- Collapsed bottom panel reopen strip -->
          <button
            v-if="preferences.consoleCollapsed"
            type="button"
            class="shrink-0 h-8 w-full bg-slate-950 border-t border-slate-800 hover:bg-slate-900 text-slate-400 hover:text-amber-300 transition-colors flex items-center justify-center gap-2 cursor-pointer"
            title="Show Output Panel (Alt+O)"
            @click="toggleConsole"
          >
            <i class="pi pi-chevron-up text-xs"></i>
            <span class="text-[10px] font-semibold tracking-wide uppercase">Output · Datasets · Reference</span>
            <i class="pi pi-chevron-up text-xs"></i>
          </button>

          <!-- Bottom Half: Global Console, Plots, Datasets & Reference Tabs -->
          <div
            v-else
            class="shrink-0 overflow-hidden"
            :style="{ height: preferences.consoleHeight + 'px' }"
          >
            <OutputConsole 
              :question="currentQuestion"
              :result="executionResult"
              :checked-ids="currentChecklist"
              v-model:active-output-tab="preferences.activeOutputTab"
              @clear-console="executionResult = undefined"
              @copy-solution="copySolutionToEditor"
              @toggle-checklist="toggleChecklistItem"
              @collapse="toggleConsole"
            />
          </div>
        </div>
      </main>
    </div>

    <!-- Running overlay hint (UI stays responsive; Python runs in worker) -->
    <div
      v-if="isPythonBusy"
      class="pointer-events-none fixed bottom-4 right-4 z-40 px-3 py-2 rounded-lg bg-slate-900/95 border border-amber-500/40 text-xs text-amber-200 shadow-xl flex items-center gap-2"
    >
      <i class="pi pi-spin pi-spinner text-amber-400"></i>
      <span>Running Python in background worker… large plots (pairplot) can take a while</span>
    </div>

    <!-- Modals -->
    <TimerWidget 
      v-model:visible="showTimerModal" 
      @update-display="timerDisplay = $event" 
    />

    <OnboardingModal 
      v-model:visible="showOnboardingModal" 
    />

    <KeyboardShortcutsModal 
      v-model:visible="showShortcutsModal" 
    />

    <ExportImportModal 
      v-model:visible="showExportModal" 
      @export="handleExport"
      @import="handleImport"
      @reset-all="handleResetAll"
    />

    <SnapshotHistoryModal 
      v-model:visible="showSnapshotsModal"
      :snapshots="currentAnswerState?.snapshots"
      @restore="restoreSnapshot"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import HeaderNavbar from './components/HeaderNavbar.vue';
import QuestionSidebar from './components/QuestionSidebar.vue';
import PromptMarkdown from './components/PromptMarkdown.vue';
import CodeMirrorEditor from './components/CodeMirrorEditor.vue';
import NotebookCellRunner from './components/NotebookCellRunner.vue';
import OutputConsole from './components/OutputConsole.vue';
import GlobalPyodideLoader from './components/GlobalPyodideLoader.vue';
import TimerWidget from './components/TimerWidget.vue';
import OnboardingModal from './components/OnboardingModal.vue';
import KeyboardShortcutsModal from './components/KeyboardShortcutsModal.vue';
import ExportImportModal from './components/ExportImportModal.vue';
import SnapshotHistoryModal from './components/SnapshotHistoryModal.vue';
import ResizeHandle from './components/ResizeHandle.vue';

import { QUESTIONS, Question } from './data/questions';
import { 
  QuestionAnswerState, 
  UIPreferences, 
  ExecutionResult, 
  PyodideStatus, 
  StudentStatus,
  NotebookCell
} from './types';

import { 
  getPreferences, 
  savePreferences, 
  getAnswerState, 
  saveAnswerState, 
  getAllAnswerStates, 
  addSnapshot, 
  exportAllProgressJSON, 
  importProgressJSON, 
  resetAllData,
  serializeNotebookCells,
} from './services/storage';

import { 
  initPyodide, 
  runPythonCode, 
  mountDatasetInVFS, 
  onPyodideStatusChange,
  onPythonBusyChange,
} from './services/pyodide';

// Reactive State
const preferences = ref<UIPreferences>({
  ...getPreferences(),
  editorMode: getPreferences().editorMode || 'notebook',
});

const activeQuestionId = ref<string>(preferences.value.lastQuestionId || QUESTIONS[0].id);

const currentCode = ref<string>('');
const currentStatus = ref<StudentStatus>('not_started');
const currentChecklist = ref<string[]>([]);
const currentAnswerState = ref<QuestionAnswerState | undefined>(undefined);
const notebookCells = ref<NotebookCell[]>([]);
const questionReady = ref(false);

const executionResult = ref<ExecutionResult | undefined>(undefined);
const isRunning = ref(false);
const isPythonBusy = ref(false);
const lastSavedTime = ref<string>('');

let autosaveTimer: number | null = null;

const answerStatuses = ref<Record<string, StudentStatus>>({});

// Modals State
const showTimerModal = ref(false);
const showOnboardingModal = ref(false);
const showShortcutsModal = ref(false);
const showExportModal = ref(false);
const showSnapshotsModal = ref(false);

const timerDisplay = ref('00:00');

// Pyodide State
const pyodideStatus = ref<PyodideStatus>('uninitialized');
const pyodideStatusMessage = ref<string>('Initializing WebAssembly runtime...');

const currentQuestion = computed<Question>(() => {
  return QUESTIONS.find(q => q.id === activeQuestionId.value) || QUESTIONS[0];
});

const doneCount = computed(() => {
  return Object.values(answerStatuses.value).filter(s => s === 'done').length;
});

async function retryInitPyodide() {
  pyodideStatus.value = 'loading';
  pyodideStatusMessage.value = 'Retrying Pyodide load...';
  try {
    await initPyodide();
  } catch (e: any) {
    pyodideStatus.value = 'error';
    pyodideStatusMessage.value = e.message || 'Error loading runtime';
  }
}

// Load question state into editor & notebook cells
async function loadQuestionData(qId: string) {
  questionReady.value = false;

  const state = await getAnswerState(qId);
  if (state) {
    currentAnswerState.value = state;
    currentCode.value = state.code || '';
    // Prefer persisted cells; fall back to splitting saved code into cells
    if (state.cells && state.cells.length > 0) {
      notebookCells.value = serializeNotebookCells(state.cells);
    } else if (state.code?.trim()) {
      notebookCells.value = [];
      // NotebookCellRunner will parse initialCode into cells on mount
    } else {
      notebookCells.value = [];
      currentCode.value = (QUESTIONS.find(item => item.id === qId) || QUESTIONS[0]).starterCode;
    }
    currentStatus.value = state.status;
    currentChecklist.value = state.checklistChecked || [];
  } else {
    const q = QUESTIONS.find(item => item.id === qId) || QUESTIONS[0];
    currentCode.value = q.starterCode;
    notebookCells.value = [];
    currentStatus.value = 'not_started';
    currentChecklist.value = [];
    currentAnswerState.value = undefined;
  }

  questionReady.value = true;

  // Pre-mount attached datasets into Pyodide virtual filesystem
  if (currentQuestion.value.datasets?.length) {
    for (const ds of currentQuestion.value.datasets) {
      mountDatasetInVFS(ds.name, ds.url);
    }
  }
}

function buildAnswerPayload(overrides: Partial<QuestionAnswerState> = {}): QuestionAnswerState {
  return {
    questionId: activeQuestionId.value,
    code: currentCode.value,
    cells: serializeNotebookCells(notebookCells.value),
    status: currentStatus.value,
    checklistChecked: [...currentChecklist.value],
    updatedAt: new Date().toISOString(),
    snapshots: currentAnswerState.value?.snapshots || [],
    ...overrides,
  };
}

async function persistCurrentAnswer(markInProgress = true) {
  if (markInProgress && currentStatus.value === 'not_started' && (currentCode.value.trim() || notebookCells.value.length > 0)) {
    currentStatus.value = 'in_progress';
  }

  const payload = buildAnswerPayload();
  try {
    await saveAnswerState(payload);
    currentAnswerState.value = payload;
    lastSavedTime.value = new Date().toLocaleTimeString();
  } catch (e) {
    console.error('Failed to autosave notebook:', e);
  }
}

function scheduleAutosave() {
  if (autosaveTimer) {
    window.clearTimeout(autosaveTimer);
  }
  autosaveTimer = window.setTimeout(() => {
    autosaveTimer = null;
    persistCurrentAnswer();
  }, 400);
}

async function flushAutosave() {
  if (autosaveTimer) {
    window.clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
  await persistCurrentAnswer();
}

// Refresh overall status map
async function refreshAnswerStatuses() {
  const all = await getAllAnswerStates();
  const map: Record<string, StudentStatus> = {};
  for (const q of QUESTIONS) {
    map[q.id] = all[q.id]?.status || 'not_started';
  }
  answerStatuses.value = map;
}

// Switch Active Question INSTANTLY
async function switchQuestion(newId: string) {
  if (newId === activeQuestionId.value) return;

  // Persist current notebook/script, then unmount editor before changing id
  await flushAutosave();
  questionReady.value = false;
  await nextTick();

  activeQuestionId.value = newId;
  preferences.value = savePreferences({ lastQuestionId: newId });
  executionResult.value = undefined;

  await loadQuestionData(newId);
}

function onNotebookCodeUpdated(code: string) {
  if (!questionReady.value) return;
  currentCode.value = code;
}

function onCellsUpdated(updatedCells: NotebookCell[]) {
  if (!questionReady.value) return;
  notebookCells.value = serializeNotebookCells(updatedCells);
  scheduleAutosave();
}

function onScriptCodeUpdated(code: string) {
  currentCode.value = code;
  scheduleAutosave();
}

// Manual or debounced save
async function manualSave(note: string = 'Manual Save') {
  await flushAutosave();
  const updated = await addSnapshot(
    activeQuestionId.value,
    currentCode.value,
    note,
    notebookCells.value
  );
  currentAnswerState.value = {
    ...updated,
    cells: serializeNotebookCells(notebookCells.value),
  };
  answerStatuses.value[activeQuestionId.value] = currentStatus.value;
  lastSavedTime.value = new Date().toLocaleTimeString();
  await refreshAnswerStatuses();
}

// Execute Code (Script View)
async function handleRunCode() {
  if (isRunning.value) return;
  isRunning.value = true;
  preferences.value.activeOutputTab = 'console';

  try {
    await flushAutosave();
    const res = await runPythonCode(currentCode.value, true);
    executionResult.value = res;

    if (res.plots?.length > 0) {
      preferences.value.activeOutputTab = 'plots';
    }

    if (currentStatus.value === 'not_started') {
      currentStatus.value = 'in_progress';
    }

    await addSnapshot(
      activeQuestionId.value,
      currentCode.value,
      res.error ? 'Run Error' : 'Successful Run',
      notebookCells.value
    );
    await refreshAnswerStatuses();
  } catch (e: any) {
    console.error('Execution failure:', e);
  } finally {
    isRunning.value = false;
  }
}

// Editor Mode Toggle
async function setEditorMode(mode: 'notebook' | 'single') {
  await flushAutosave();
  preferences.value = savePreferences({ editorMode: mode });
}

async function toggleEditorMode() {
  const newMode = preferences.value.editorMode === 'notebook' ? 'single' : 'notebook';
  await setEditorMode(newMode);
}

// Update Self-Assessment Status
async function updateStatus() {
  await persistCurrentAnswer(false);
  await refreshAnswerStatuses();
}

// Toggle Checklist Item
async function toggleChecklistItem(id: string) {
  const idx = currentChecklist.value.indexOf(id);
  if (idx > -1) {
    currentChecklist.value.splice(idx, 1);
  } else {
    currentChecklist.value.push(id);
  }
  await persistCurrentAnswer(false);
}

// Reset Starter Code
function resetStarterCode() {
  if (confirm('Reset code to starter template? Your current edits will be saved in history snapshots.')) {
    currentCode.value = currentQuestion.value.starterCode;
    notebookCells.value = [];
    // Remount notebook with starter by toggling ready briefly
    questionReady.value = false;
    requestAnimationFrame(() => {
      questionReady.value = true;
      manualSave('Reset to starter code');
    });
  }
}

// Copy Reference Solution
function copySolutionToEditor(solutionCode: string) {
  if (confirm('Replace current editor content with the reference solution? Your previous code is saved in history.')) {
    currentCode.value = solutionCode;
    notebookCells.value = [];
    questionReady.value = false;
    requestAnimationFrame(() => {
      questionReady.value = true;
      manualSave('Copied Reference Solution');
    });
  }
}

// Restore Snapshot
function restoreSnapshot(code: string) {
  currentCode.value = code;
  notebookCells.value = [];
  questionReady.value = false;
  requestAnimationFrame(() => {
    questionReady.value = true;
    manualSave('Restored Snapshot');
  });
}

// UI Toggles & layout
function toggleSidebar() {
  const collapsed = !preferences.value.sidebarCollapsed;
  preferences.value = savePreferences({
    sidebarCollapsed: collapsed,
    focusMode: collapsed,
  });
}

function toggleConsole() {
  preferences.value = savePreferences({
    consoleCollapsed: !preferences.value.consoleCollapsed,
  });
}

function onSidebarResize(delta: number) {
  const next = Math.min(480, Math.max(220, preferences.value.sidebarWidth + delta));
  preferences.value.sidebarWidth = next;
}

function onPromptResize(delta: number) {
  // Convert pixel delta into % of the top split container (approx via main width)
  const main = document.querySelector('main');
  const width = main?.clientWidth || window.innerWidth;
  const deltaPct = (delta / width) * 100;
  const next = Math.min(70, Math.max(25, preferences.value.promptPercent + deltaPct));
  preferences.value.promptPercent = next;
}

function onConsoleResize(delta: number) {
  const next = Math.min(560, Math.max(140, preferences.value.consoleHeight - delta));
  preferences.value.consoleHeight = next;
}

function persistLayout() {
  preferences.value = savePreferences({
    sidebarWidth: preferences.value.sidebarWidth,
    promptPercent: preferences.value.promptPercent,
    consoleHeight: preferences.value.consoleHeight,
    sidebarCollapsed: preferences.value.sidebarCollapsed,
    consoleCollapsed: preferences.value.consoleCollapsed,
  });
}

function toggleTheme() {
  const newTheme = preferences.value.theme === 'dark' ? 'light' : 'dark';
  preferences.value = savePreferences({ theme: newTheme });
}

function resetLayout() {
  preferences.value = savePreferences({
    sidebarCollapsed: false,
    focusMode: false,
    sidebarWidth: 320,
    promptPercent: 50,
    consoleHeight: 288,
    consoleCollapsed: false,
    activeOutputTab: 'console',
  });
}

// Export / Import JSON
async function handleExport() {
  const jsonStr = await exportAllProgressJSON();
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `UNIBO_ML_Exam_Progress_${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

async function handleImport(jsonStr: string) {
  const ok = await importProgressJSON(jsonStr);
  if (ok) {
    alert('Progress imported successfully!');
    showExportModal.value = false;
    await loadQuestionData(activeQuestionId.value);
    await refreshAnswerStatuses();
  } else {
    alert('Failed to import JSON file. Please check format.');
  }
}

async function handleResetAll() {
  await resetAllData();
  alert('All local progress data has been reset.');
  showExportModal.value = false;
  location.reload();
}

// Global Keyboard Shortcuts Handler
function handleKeyDown(e: KeyboardEvent) {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

  if (e.altKey && e.key.toLowerCase() === 'b') {
    e.preventDefault();
    toggleSidebar();
  }

  if (e.altKey && e.key.toLowerCase() === 'o') {
    e.preventDefault();
    toggleConsole();
  }

  if (e.altKey && e.key.toLowerCase() === 'n') {
    e.preventDefault();
    const idx = QUESTIONS.findIndex(q => q.id === activeQuestionId.value);
    if (idx < QUESTIONS.length - 1) {
      switchQuestion(QUESTIONS[idx + 1].id);
    }
  }

  if (e.altKey && e.key.toLowerCase() === 'p') {
    e.preventDefault();
    const idx = QUESTIONS.findIndex(q => q.id === activeQuestionId.value);
    if (idx > 0) {
      switchQuestion(QUESTIONS[idx - 1].id);
    }
  }
}

onMounted(async () => {
  onPyodideStatusChange((st, msg) => {
    pyodideStatus.value = st;
    pyodideStatusMessage.value = msg || st;
  });

  onPythonBusyChange((busy) => {
    isPythonBusy.value = busy;
  });

  initPyodide().catch(() => {});

  await refreshAnswerStatuses();
  await loadQuestionData(activeQuestionId.value);

  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('beforeunload', flushAutosave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushAutosave();
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('beforeunload', flushAutosave);
  flushAutosave();
});
</script>
